import React, {
	useState,
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
} from 'react';
import style from './intents-list.module.scss';
import AddIcon from '../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { AddIntent } from '../components';
import { useQuery, useAppAndBranchId, useMutate, usePrevious, usePermissions } from '../../../hooks';
import { api } from '../api';
import { Intent } from '../types';
import { useToast } from '../../../../../../../helpers/commonFunction';
import { Icon, Spin } from 'antd'
import { errorMessage } from '../../../utils/error';

type Props = {
	intentId: string;
	setIntentId: Dispatch<SetStateAction<string>>;
};

export function IntentsList(props: Props) {
	const { ErrorToast } = useToast();
	const { intentId, setIntentId } = props;
	const [isOpen, setIsOpen] = useState(false);
	const [appId, branchId] = useAppAndBranchId();
	const [intentName, setIntentName] = useState('');
	const { mutate: addMutate, loading: addLoading } = useMutate(api.addSchema);
	const isDefaultSelectedRef = useRef(false);
	const [error, setError] = useState('');
	const prevBranchId = usePrevious(branchId)
	const canCreate = usePermissions('nlu.Create')
  
	const { data: intents, refetch, loading } = useQuery<Intent[]>(() => {
		return api.getSchema(appId, branchId, 'intent');
	}, branchId);

	function validateIntent(intent: string) {
		if (!intent.length) {
			setError('Enter intent name');
			return false;
		}

		const isExists = intents?.findIndex((el) => el.name === intent) !== -1;
		if (isExists) {
			setError('Intent name already exists');
			return false;
		}

		return true;
	}

	// on mount, select the first intent by default
	useEffect(() => {
		if (
			!isDefaultSelectedRef.current &&
			Array.isArray(intents)
		) {
		  const intentId = intents.length ? intents[0].id : ""
			setIntentId(intentId);
			isDefaultSelectedRef.current = true;
		}
	}, [intents]);

	// reset local state whenever a new branch is selected
	useEffect(() => {
	  if(branchId && prevBranchId !== branchId) {
		  isDefaultSelectedRef.current = false
		}
	}, [branchId])

	return (
		<div className={style.intentsList}>
			{canCreate && (
				<button className={style.add} onClick={() => setIsOpen(true)}>
					<img src={AddIcon} alt="add intent" />
					<span>Add Intent</span>
				</button>
			)}

			{loading && (
				<Spin indicator={<Icon type="loading" style={{ fontSize: 24, marginTop: '1rem', marginLeft: '4rem', color: '#15808C' }} spin />} />
			)}

			{!loading && (
				<div className={style.intents}>
					{intents?.map((intent) => {
						return (
							<button
								onClick={() => {
									setIntentId(intent.id);
								}}
								data-selected={intent.id === intentId}
								key={intent.id}
								className={style.intent}
							>
							  <span title={intent.name} className="text-ellipsis">{intent.name}</span>
							</button>
						);
					})}
				</div>
			)}

			<AddIntent
				error={error}
				onIntentNameChange={(intentName) => {
					setError('');
					setIntentName(intentName);
				}}
				loading={addLoading}
				isOpen={isOpen}
				onCancel={() => {
					setIsOpen(false);
					setError('');
				}}
				onOk={async () => {
					if (!validateIntent(intentName.trim())) {
						return;
					}
					try {
						await addMutate(appId, {
							name: intentName.trim(),
							branchId,
							type: 'intent',
						});
						refetch();
						setIsOpen(false);
					} catch (err) {
						ErrorToast(errorMessage("save", "intent", err.code));
					}
				}}
			/>
		</div>
	);
}
